/**
 * TURNOVER — who moved in, who moved out, and what the units look like now.
 *
 * ── ONE Y-AXIS. PEOPLE. ──
 * Move-ins, move-outs and the net are all counts of the same thing, so they
 * share one scale. A second y-axis would let the net line cross the bars at a
 * point that means nothing: two scales put the intersection wherever the
 * auto-ranging happened to fall, and a reader takes a crossing point as a fact.
 * The net is drawn as a LINE rather than a third bar because it is a derived
 * figure — it is the gap between the two bars, and drawing the gap as a bar of
 * its own triples the ink for one subtraction.
 *
 * ── THREE OCCUPANCY CLASSES, AND "UNRECORDED" IS NOT "VACANT" ──
 * A unit nobody has ever entered an occupancy for is indistinguishable from an
 * empty one from the outside, and only one of those is a fact. Counting it as
 * vacant is exactly how an occupancy rate becomes fiction — so the three classes
 * are rendered as three, the unrecorded share is drawn as ABSENCE (a hatch, not
 * a solid fill), and this panel prints NO single occupancy percentage at all.
 *
 * ── THE MOVE-OUT REASONS FOLD AT FOUR ──
 * Every reason is compared against every other, which is the all-pairs case.
 * "(not recorded)" is pulled OUT of that fold entirely and given the bucket
 * neutral: it is the absence of an answer, not a reason people leave, and it is
 * usually the largest row.
 */
import React, { useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AnalyticsTurnover } from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { PanelShell, ChartShellState, ScrollableChart } from '../charts/chart-shell';
import { SectionEmpty } from '../charts/chart-empty';
import { DashTooltip } from '../charts/chart-tooltip';
import { OTHER_TONE, foldToCap, occupancyTone } from '../charts/chart-palette';
import {
  ChartArea,
  DataTable,
  PanelToggle,
  TableScroller,
  Td,
  Th,
} from '../charts/chart.styled';
import {
  BAR_RADIUS,
  SYNC_ID,
  cursor,
  format,
  grid,
  lineMark,
  margins,
  soloPoint,
  xAxisTime,
  yAxisCount,
} from '../charts/chart-theme';
import { formatDateOnly, zoneNote } from '../../../../../function-utils/building-time';
import {
  BarFill,
  BarLabel,
  BarList,
  BarRow,
  BarRowItem,
  BarTrack,
  BarValue,
  BarValueGroup,
  KeyItem,
  KeyRow,
  KeySwatch,
  KeySwatchHatched,
  MeterMeta,
  PanelSection,
  PanelSectionHead,
  PanelSectionTitle,
  SectionCaption,
  StackItem,
  StackList,
  StackSeg,
  StackSegUnrecorded,
  StackTrack,
} from './panels.styled';
import { hubNotes } from './panel-utils';

const PANEL_ID = 'turnover';

/** The server's own spelling for "nobody wrote a reason down". Recognised rather
 *  than re-created: it is a bucket the ENGINE named, and matching on it is how
 *  the renderer knows to give it the neutral treatment instead of a series
 *  colour. */
export const NOT_RECORDED = '(not recorded)';

const CLASS_WORD: Record<string, string> = {
  occupied: 'occupied',
  vacant: 'vacant',
  unrecorded: 'unrecorded',
};

export interface TurnoverPanelProps {
  state: ChartShellState;
  data?: AnalyticsTurnover;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const TurnoverPanel: React.FC<TurnoverPanelProps> = ({
  state,
  data,
  onExportCsv,
}) => {
  const [showTable, setShowTable] = useState(false);

  /* perBuildingWindows is FALSE on this route by construction — a bare DATE
     column has no zone to resolve per building — so hubNotes adds no cross-zone
     caption here at all. The server's own note carries the specific version of
     that fact, and the top bar's caption carries the general one, so a third
     sentence would be the same claim a third time. */
  const notes = data ? hubNotes(data) : [];

  /* THE REASONS, SPLIT BEFORE THEY ARE FOLDED. */
  const recorded = (data?.reasons || []).filter(row => row.reason !== NOT_RECORDED);
  const notRecorded = (data?.reasons || []).find(row => row.reason === NOT_RECORDED);
  const reasonSlices = foldToCap(
    recorded.map(row => ({ name: row.reason, value: row.count }))
  );
  const reasonTotal =
    reasonSlices.reduce((sum, slice) => sum + slice.value, 0) + (notRecorded?.count || 0);
  const reasonMax = Math.max(
    1,
    ...reasonSlices.map(slice => slice.value),
    notRecorded?.count || 0
  );

  const hasMoves = !!data && data.series.some(row => row.moveIns || row.moveOuts);

  return (
    <PanelShell
      id={PANEL_ID}
      title="Turnover"
      wide
      state={state}
      notes={notes}
      onExportCsv={onExportCsv}
      caveat="Move-ins and move-outs per period, with the net on ONE scale — people. These are DATES ON A RECORD, not observations: a move-in is a record saying somebody started living somewhere, not anybody having watched them arrive."
      emptyReason={
        data && !data.series.length && !data.occupancy.length
          ? {
              title: 'No occupancy records in scope',
              text: 'Nothing you can see has a move-in or move-out record, and there are no active units to classify.',
            }
          : null
      }
    >
      {data ? (
        <>
          {/* ── the direction of travel ── */}
          <PanelSection>
            <PanelSectionHead>
              <PanelSectionTitle>
                Moved in, moved out, net — per {data.bucket}
              </PanelSectionTitle>
              {data.series.length ? (
                <PanelToggle
                  type="button"
                  active={showTable}
                  aria-pressed={showTable}
                  onClick={() => setShowTable(value => !value)}
                >
                  {showTable ? 'Chart' : 'Table'}
                </PanelToggle>
              ) : null}
            </PanelSectionHead>
            <MeterMeta data-testid="turnover-totals">
              <span>
                {data.moveIns.toLocaleString()} in · {data.moveOuts.toLocaleString()} out
                ·{' '}
                <strong>
                  {data.net > 0 ? '+' : ''}
                  {data.net.toLocaleString()} net
                </strong>
                {data.window.from && data.window.to
                  ? ` — calendar dates ${formatDateOnly(data.window.from)} to ${formatDateOnly(data.window.to)}, on ${zoneNote(data.window.tz) || data.window.tz}`
                  : ''}
                .
              </span>
            </MeterMeta>

            {!hasMoves ? (
              <SectionEmpty
                title="Nobody moved in or out in this period"
                text="Every bucket is zero. A period with no turnover is a normal answer, not a missing figure."
              />
            ) : showTable ? (
              <TableScroller>
                <DataTable data-testid="turnover-table">
                  <thead>
                    <tr>
                      <Th scope="col">{format.humanise(data.bucket)}</Th>
                      <Th scope="col" numeric>
                        Moved in
                      </Th>
                      <Th scope="col" numeric>
                        Moved out
                      </Th>
                      <Th scope="col" numeric>
                        Net
                      </Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.series.map(row => (
                      <tr key={row.bucket}>
                        <Td>{format.dayKey(row.bucket)}</Td>
                        <Td numeric>{row.moveIns.toLocaleString()}</Td>
                        <Td numeric>{row.moveOuts.toLocaleString()}</Td>
                        <Td numeric>
                          {row.net > 0 ? '+' : ''}
                          {row.net.toLocaleString()}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </TableScroller>
            ) : (
              <>
                <ScrollableChart>
                  <ChartArea $aspect={3} $minHeight={240} $minWidth={560}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart accessibilityLayer
                        data={data.series}
                        margin={margins.default}
                        barGap={2}
                        syncId={SYNC_ID}
                      >
                        <CartesianGrid {...grid} />
                        <XAxis
                          dataKey="bucket"
                          tickFormatter={format.dayKey}
                          {...xAxisTime}
                        />
                        {/* ONE AXIS. Two scales would put the net line's crossing
                            point wherever auto-ranging fell, and a reader takes a
                            crossing point as a fact. */}
                        <YAxis {...yAxisCount} />
                        {/* THE ZERO RULE, DRAWN. The net goes negative whenever
                            move-outs lead, and a line crossing an implied
                            baseline is a line whose sign a reader has to infer
                            from the y-axis ticks. */}
                        <ReferenceLine y={0} stroke={palette.lineStrong} />
                        <Tooltip
                          content={
                            <DashTooltip
                              series={[
                                {
                                  key: 'moveIns',
                                  label: 'moved in',
                                  tone: dataViz.series[0],
                                },
                                {
                                  key: 'moveOuts',
                                  label: 'moved out',
                                  tone: dataViz.series[1],
                                },
                                { key: 'net', label: 'net', tone: dataViz.series[2] },
                              ]}
                              labelOf={raw => format.dayKey(String(raw))}
                              note="Dates on a record, not observations — somebody typed these."
                            />
                          }
                          cursor={cursor.bar}
                        />
                        <Bar
                          dataKey="moveIns"
                          name="moved in"
                          fill={dataViz.series[0]}
                          radius={BAR_RADIUS}
                          isAnimationActive={false}
                        />
                        <Bar
                          dataKey="moveOuts"
                          name="moved out"
                          fill={dataViz.series[1]}
                          radius={BAR_RADIUS}
                          isAnimationActive={false}
                        />
                        <Line
                          dataKey="net"
                          name="net"
                          {...lineMark}
                          {...soloPoint(data.series.length, dataViz.series[2])}
                          stroke={dataViz.series[2]}
                          isAnimationActive={false}
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </ChartArea>
                </ScrollableChart>
                {/* THREE SERIES, SO THERE IS A KEY — and the labels wear ink while
                    the chip carries the identity. */}
                <KeyRow data-testid="turnover-key">
                  <KeyItem>
                    <KeySwatch tone={dataViz.series[0]} aria-hidden="true" />
                    Moved in
                  </KeyItem>
                  <KeyItem>
                    <KeySwatch tone={dataViz.series[1]} aria-hidden="true" />
                    Moved out
                  </KeyItem>
                  <KeyItem>
                    <KeySwatch tone={dataViz.series[2]} aria-hidden="true" />
                    Net (moved in minus moved out)
                  </KeyItem>
                </KeyRow>
              </>
            )}
          </PanelSection>

          {/* ── why they left ── */}
          <PanelSection>
            <PanelSectionHead>
              <PanelSectionTitle>Why they left</PanelSectionTitle>
              <SectionCaption>
                Top four reasons named; the rest folded into one bucket.
              </SectionCaption>
            </PanelSectionHead>
            {reasonTotal ? (
              <BarList data-testid="turnover-reasons">
                {reasonSlices.map(slice => (
                  <BarRowItem key={slice.name}>
                    <BarRow as="div">
                      <BarLabel>{slice.name}</BarLabel>
                      <BarValueGroup>
                        <BarValue>{slice.value.toLocaleString()}</BarValue>
                      </BarValueGroup>
                      <BarTrack>
                        <BarFill
                          tone={slice.color}
                          pct={(slice.value / reasonMax) * 100}
                        />
                      </BarTrack>
                    </BarRow>
                  </BarRowItem>
                ))}
                {/* THE ABSENCE, LAST AND NEUTRAL. A reason nobody wrote down is
                    not a reason, so it never takes a series colour and is never
                    summarised as though the recorded ones were typical of it. */}
                {notRecorded ? (
                  <BarRowItem>
                    <BarRow as="div">
                      <BarLabel data-testid="turnover-not-recorded">
                        No reason recorded
                      </BarLabel>
                      <BarValueGroup>
                        <BarValue>{notRecorded.count.toLocaleString()}</BarValue>
                      </BarValueGroup>
                      <BarTrack>
                        <BarFill
                          tone={OTHER_TONE}
                          pct={(notRecorded.count / reasonMax) * 100}
                        />
                      </BarTrack>
                    </BarRow>
                  </BarRowItem>
                ) : null}
              </BarList>
            ) : (
              <SectionEmpty
                title="No move-outs to explain"
                text="Nobody moved out in this period, so there is no reason distribution."
              />
            )}
            {notRecorded ? (
              <MeterMeta>
                <span>
                  {data.reasonsRecorded.toLocaleString()} of{' '}
                  {reasonTotal.toLocaleString()} move-outs carry a reason. The rest are
                  counted and reported as unrecorded — never inferred, and never
                  summarised as though the recorded ones were typical.
                </span>
              </MeterMeta>
            ) : null}
          </PanelSection>

          {/* ── units as they are now ── */}
          <PanelSection>
            <PanelSectionHead>
              <PanelSectionTitle>
                Units right now, in {data.classes.length} classes
              </PanelSectionTitle>
              <SectionCaption>
                As of now, not as of the end of the period. Active units only — an
                archived unit is inventory history.
              </SectionCaption>
            </PanelSectionHead>
            {/* NO SINGLE OCCUPANCY PERCENTAGE, AND THE REASON IS PRINTED. */}
            <SectionCaption data-testid="occupancy-no-rate">
              There is deliberately <strong>no occupancy rate</strong> here.{' '}
              <strong>Unrecorded</strong> means nobody has ever entered an occupancy for
              that unit — indistinguishable from empty from the outside, and only one of
              those is a fact. Folding it into <strong>vacant</strong> to produce a
              single percentage is how an occupancy rate becomes fiction.
            </SectionCaption>
            {data.occupancy.length ? (
              <>
                <StackList data-testid="occupancy-list">
                  {data.occupancy.map(building => (
                    <StackItem key={building.buildingId}>
                      <MeterMeta>
                        <span>
                          <strong>{building.building}</strong>
                        </span>
                        <span>
                          {building.units.toLocaleString()} active unit
                          {building.units === 1 ? '' : 's'}
                        </span>
                      </MeterMeta>
                      <StackTrack
                        role="img"
                        aria-label={`${building.building}: ${data.classes
                          .map(
                            cls =>
                              `${(building.classes[cls] || 0).toLocaleString()} ${
                                CLASS_WORD[cls] || cls
                              }`
                          )
                          .join(', ')}`}
                      >
                        {data.classes.map(cls => {
                          const count = building.classes[cls] || 0;
                          const pct =
                            building.units > 0 ? (count / building.units) * 100 : 0;

                          // A hatch for "we do not know", a solid fill for the two
                          // facts. Same component, different ground.
                          return cls === 'unrecorded' ? (
                            <StackSegUnrecorded
                              key={cls}
                              pct={pct}
                              data-testid="occupancy-seg-unrecorded"
                            />
                          ) : (
                            <StackSeg
                              key={cls}
                              pct={pct}
                              tone={occupancyTone(cls)}
                              data-testid={`occupancy-seg-${cls}`}
                            />
                          );
                        })}
                      </StackTrack>
                      <MeterMeta>
                        {data.classes.map(cls => (
                          <span key={cls}>
                            {(building.classes[cls] || 0).toLocaleString()}{' '}
                            {CLASS_WORD[cls] || cls}
                          </span>
                        ))}
                      </MeterMeta>
                    </StackItem>
                  ))}
                </StackList>
                {/* THE WORD BESIDE EVERY MARK. The hatch says "absence" and the
                    label says which absence. */}
                <KeyRow data-testid="occupancy-key">
                  {data.classes.map(cls => (
                    <KeyItem key={cls}>
                      {cls === 'unrecorded' ? (
                        <KeySwatchHatched aria-hidden="true" />
                      ) : (
                        <KeySwatch tone={occupancyTone(cls)} aria-hidden="true" />
                      )}
                      {CLASS_WORD[cls] || cls}
                      {cls === 'unrecorded'
                        ? ' — no occupancy record has ever existed for this unit'
                        : ''}
                    </KeyItem>
                  ))}
                </KeyRow>
              </>
            ) : (
              <SectionEmpty
                title="No active units in scope"
                text="Nothing you can see has an active unit, so there is nothing to classify."
              />
            )}
          </PanelSection>
        </>
      ) : null}
    </PanelShell>
  );
};
